
import React, { useState } from 'react';
import { Order, OrderStatus } from '../types';
import { fetchVesselData } from '../services/marineTrafficService';
// Added comment above fix: Consolidating all used lucide-react icons into a single import at the top of the file to fix missing references and clean up the component structure
import { 
  Ship, RefreshCw, ExternalLink, Map, Anchor, 
  Compass, Navigation, Clock, AlertCircle,
  Loader2, Radar, Hash, Calendar as CalendarIcon 
} from 'lucide-react';

interface AutoTrackingProps {
  orders: Order[];
  onUpdateOrder: (order: Order) => void;
}

export const AutoTracking: React.FC<AutoTrackingProps> = ({ orders, onUpdateOrder }) => {
  // Broaden criteria: Any order with a vessel name that isn't finished (Cleared or Cancelled)
  const trackedOrders = orders.filter(
    o => o.vesselPlanned && 
    o.currentStatus !== OrderStatus.CLEARED && 
    o.currentStatus !== OrderStatus.CANCELLED
  );

  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});

  const handleSync = async (order: Order) => {
    if (!order.vesselPlanned) return;

    setLoadingMap(prev => ({ ...prev, [order.id]: true }));
    
    try {
      const data = await fetchVesselData(order.vesselPlanned);
      if (data) {
        onUpdateOrder({
          ...order,
          trackingData: data
        });
      }
    } catch (error) {
      console.error("Failed to sync vessel data", error);
    } finally {
      setLoadingMap(prev => ({ ...prev, [order.id]: false }));
    }
  };

  const handleSyncAll = async () => {
    for (const order of trackedOrders) {
      await handleSync(order);
    }
  };

  if (trackedOrders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 bg-white rounded-3xl border border-slate-200 shadow-sm text-center p-8 animate-in fade-in duration-500">
        <div className="bg-slate-50 p-6 rounded-[2rem] mb-6">
          <Ship className="w-12 h-12 text-slate-300" />
        </div>
        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">No Trackable Shipments</h3>
        <p className="text-slate-500 mt-2 max-w-sm font-medium">
          Tracking requires a <span className="font-bold text-indigo-600">Vessel Name</span> to be assigned to an active order. Update your order details to enable live telemetry.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-3xl -mr-20 -mt-20"></div>
        <div className="relative z-10">
          <h2 className="text-2xl font-black text-white flex items-center gap-3 uppercase tracking-tight">
            <Navigation className="w-7 h-7 text-indigo-400" />
            Vessel Telemetry
          </h2>
          <p className="text-slate-400 text-sm mt-1 font-medium">
            Real-time satellite tracking for <span className="text-white font-bold">{trackedOrders.length}</span> active branch consignments.
          </p>
        </div>
        <button 
          onClick={handleSyncAll}
          className="relative z-10 px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-all shadow-xl shadow-indigo-900/40 active:scale-95"
        >
          <RefreshCw className="w-4 h-4" />
          Sync Entire Fleet
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {trackedOrders.map(order => {
          const isLoading = loadingMap[order.id];
          // Robust check: trackingData might be {} or [] from API if empty
          const hasData = order.trackingData && typeof order.trackingData === 'object' && 'vesselName' in order.trackingData;
          const data = order.trackingData;

          return (
            <div key={order.id} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden hover:shadow-xl hover:border-indigo-200 transition-all group flex flex-col">
              {/* Card Header */}
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center shrink-0">
                <div>
                  <h3 className="font-black text-slate-800 uppercase tracking-tight text-lg">{order.vesselPlanned}</h3>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-0.5 flex items-center gap-2">
                    <Hash className="w-3 h-3 text-indigo-400" /> {order.id} • <Anchor className="w-3 h-3 text-indigo-400" /> {order.portOfDestination}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                   <button 
                    onClick={() => handleSync(order)}
                    disabled={isLoading}
                    className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-xl border border-transparent hover:border-slate-100 transition-all shadow-sm group-hover:border-slate-200"
                    title="Refresh Data"
                  >
                    <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin text-indigo-600' : ''}`} />
                  </button>
                  {hasData && (
                    <a 
                        href={`https://www.marinetraffic.com/en/ais/home/shipid:${data?.imo || ''}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-xl border border-transparent hover:border-slate-100 transition-all shadow-sm group-hover:border-slate-200"
                        title="View on MarineTraffic"
                    >
                        <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-8 flex-1">
                {hasData ? (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm ${data?.status?.includes('Underway') ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${data?.status?.includes('Underway') ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`}></div>
                            {data?.status || 'Active'}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter flex items-center gap-1.5 ml-auto">
                            <Clock size={12} className="text-slate-300" /> Updated: {new Date(data?.lastUpdated!).toLocaleTimeString()}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                      <div className="space-y-1.5">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <Map className="w-3 h-3 text-indigo-400" /> Lat / Lon
                        </p>
                        <p className="text-sm font-mono font-black text-slate-700">
                          {data?.latitude?.toFixed(4)}° / {data?.longitude?.toFixed(4)}°
                        </p>
                      </div>
                      <div className="space-y-1.5">
                         <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <Compass className="w-3 h-3 text-indigo-400" /> Velocity
                        </p>
                        <p className="text-sm font-black text-slate-700">
                          {data?.speed?.toFixed(1)} kn / {data?.course?.toFixed(1)}°
                        </p>
                      </div>
                      <div className="space-y-1.5">
                         <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <Anchor className="w-3 h-3 text-indigo-400" /> Next Destination
                        </p>
                        <p className="text-sm font-black text-slate-800 uppercase tracking-tight">
                          {data?.nextPort || 'PENDING'}
                        </p>
                      </div>
                      <div className="space-y-1.5">
                         <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                          <CalendarIcon className="w-3 h-3 text-indigo-400" /> ETA Stamp
                        </p>
                        <p className="text-sm font-black text-slate-800">
                          {data?.eta || 'TBD'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center py-12 text-center space-y-6 bg-slate-50/50 rounded-[2rem] border border-slate-100 shadow-inner">
                    <div className="relative">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all ${isLoading ? 'bg-indigo-600 text-white' : 'bg-white text-slate-300 shadow-sm border border-slate-100'}`}>
                            {isLoading ? <Loader2 className="w-8 h-8 animate-spin" /> : <Radar className="w-8 h-8" />}
                        </div>
                    </div>
                    <div className="max-w-[200px]">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-relaxed">
                            {isLoading ? 'Handshaking with Satellite...' : 'Live link not established for this vessel.'}
                        </p>
                    </div>
                    {!isLoading && (
                        <button 
                            onClick={() => handleSync(order)}
                            className="px-6 py-2.5 bg-white border border-slate-200 text-indigo-600 hover:border-indigo-400 hover:shadow-md rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                        >
                            Initiate Track
                        </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AutoTracking;
